<template>
    <InfiniteScroller #default="{ listPortion }" :list="usersFiltered">
        <div v-if="isFetching" class="l-padded">
            <VSpinner size="medium" :line-fg-color="spinnerColor" :speed="1" />
        </div>

        <div
            v-else-if="!usersFiltered.length"
            class="l-padded l-inline l-center t-subtle t-small"
        >
            <span>
                {{ $t('shared.noSearchResult') }}
            </span>
        </div>

        <table v-else>
            <thead>
                <tr>
                    <th>
                        {{ $t('username') }}

                        <IconButton @click="handleSort('username')">
                            <SortArrowIcon
                                :direction="
                                    sortBy === 'username' ? sortDirection : 0
                                "
                                width="10"
                                height="10"
                            />
                        </IconButton>
                    </th>

                    <th>
                        {{ $t('firstName') }}

                        <IconButton @click="handleSort('first_name')">
                            <SortArrowIcon
                                :direction="
                                    sortBy === 'first_name' ? sortDirection : 0
                                "
                                width="10"
                                height="10"
                            />
                        </IconButton>
                    </th>

                    <th>
                        {{ $t('lastName') }}

                        <IconButton @click="handleSort('last_name')">
                            <SortArrowIcon
                                :direction="
                                    sortBy === 'last_name' ? sortDirection : 0
                                "
                                width="10"
                                height="10"
                            />
                        </IconButton>
                    </th>

                    <th>
                        {{ $t('role') }}

                        <IconButton @click="handleSort('role_details.name')">
                            <SortArrowIcon
                                :direction="
                                    sortBy === 'role_details.name'
                                        ? sortDirection
                                        : 0
                                "
                                width="10"
                                height="10"
                            />
                        </IconButton>
                    </th>

                    <th>
                        {{ $t('actions') }}
                    </th>
                </tr>
            </thead>

            <template v-for="item in listPortion">
                <tr :key="item.id">
                    <td>
                        {{ item.username }}
                    </td>

                    <td>
                        {{ item.first_name }}
                    </td>

                    <td>
                        {{ item.last_name }}
                    </td>

                    <td>
                        <div class="l-inline l-center-v l-gap-1">
                            <span v-if="item.role_details">
                                {{ item.role_details.name }}
                            </span>

                            <Badge v-if="item.is_account_admin" color="primary">
                                {{ $t('admin') }}
                            </Badge>
                        </div>
                    </td>

                    <td>
                        <div class="action-group l-inline l-gap-1">
                            <div class="l-inline l-gap-1">
                                <IconButton
                                    class="action-chip action-chip--neutral"
                                    :title="
                                        $t('editActionTitle', [item.username])
                                    "
                                    @click="handleEdit(item)"
                                >
                                    <PencilIcon />
                                </IconButton>

                                <IconButton
                                    class="action-chip action-chip--static"
                                    :title="
                                        $t('resetPasswordActionTitle', [
                                            item.username,
                                        ])
                                    "
                                    @click="handlePasswordReset(item)"
                                >
                                    <PasswordResetIcon />
                                </IconButton>
                            </div>

                            <div class="action-group-danger">
                                <IconButton
                                    v-if="item.deletable"
                                    class="action-chip action-chip--danger"
                                    :title="
                                        $t('deleteActionTitle', [item.username])
                                    "
                                    @click="handleDelete(item)"
                                >
                                    <TrashBinIcon />
                                </IconButton>

                                <div
                                    v-else
                                    class="action-chip action-chip--static"
                                    :title="
                                        $t('userNotDeletableTitle', [
                                            item.username,
                                        ])
                                    "
                                >
                                    <DisabledIcon />
                                </div>
                            </div>
                        </div>
                    </td>
                </tr>
            </template>
        </table>
    </InfiniteScroller>
</template>

<script>
import { mapState } from 'vuex'
import { get } from 'lodash'
import moment from 'moment-timezone'
import VSpinner from 'vue-simple-spinner'

import { httpHelper } from '@/utils'
import { commonService } from '@/service/store.service'
import Badge from '@/components/redesigned/Badge'
import IconButton from '@/components/IconButton'
import InfiniteScroller from '@/components/InfiniteScroller'
import PasswordResetIcon from '@/components/icons/PasswordResetIcon'
import PencilIcon from '@/components/icons/PencilIcon'
import SortArrowIcon from '@/components/icons/SortArrowIcon'
import DisabledIcon from '@/components/icons/DisabledIcon'
import TrashBinIcon from '@/components/icons/TrashBinIcon'
import UserEditModal from '@/components/redesigned/UserEditModal'

const defaultSortColumn = 'username'
const defaultSortDirection = 1

export default {
    name: 'UsersTable',
    components: {
        Badge,
        IconButton,
        InfiniteScroller,
        DisabledIcon,
        PasswordResetIcon,
        PencilIcon,
        SortArrowIcon,
        TrashBinIcon,
        VSpinner,
    },
    props: {
        filterName: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            isFetching: false,
            sortBy: defaultSortColumn,
            sortDirection: defaultSortDirection,
            spinnerColor: process.env.VUE_APP_COLOR_PRIMARY,
        }
    },
    computed: {
        ...mapState('user', ['users']),
        usersFiltered() {
            let result = this.users

            if (this.filterName) {
                const query = this.filterName.toLowerCase()
                result = result.filter(
                    item =>
                        item.username.toLowerCase().includes(query) ||
                        item.first_name.toLowerCase().includes(query) ||
                        item.last_name.toLowerCase().includes(query)
                )
            }

            if (this.sortBy) {
                result = [...result].sort((a, b) => {
                    const aValue = get(a, this.sortBy)
                    const bValue = get(b, this.sortBy)

                    if (aValue === bValue) {
                        return 0
                    } else if (aValue == null) {
                        return 1
                    } else if (bValue == null) {
                        return -1
                    } else if (typeof aValue === 'string') {
                        return aValue.localeCompare(bValue) * this.sortDirection
                    }

                    return aValue > bValue
                        ? this.sortDirection
                        : -this.sortDirection
                })
            }

            return result
        },
    },
    methods: {
        async fetchUsers() {
            this.isFetching = true
            await commonService.populateUsers()
            this.isFetching = false
        },
        formatTimestamp(timestamp) {
            return moment(timestamp).format('DD.MM.YYYY HH:mm')
        },
        async handlePasswordReset(user) {
            await httpHelper.post('/password-reset/', {
                email: user.email,
            })
            this.$notify(this.$t('passwordResetNotification'))
        },
        async handleDelete(user) {
            this.$modal.show('dialog', {
                title: this.$t('deleteUserConfirmation', [user.username]),
                buttons: [
                    {
                        title: this.$t('shared.yes'),
                        handler: async () => {
                            this.$modal.hide('dialog')
                            this.isFetching = true
                            try {
                                await httpHelper.drop(`/users/${user.id}/`)
                                await this.fetchUsers()
                                this.$notify(
                                    this.$t('deleteUserSuccess', [
                                        user.username,
                                    ])
                                )
                            } catch {
                                this.$notify({
                                    title: this.$t('shared.error'),
                                    text: this.$t('deleteUserError', [
                                        user.username,
                                    ]),
                                })
                            } finally {
                                this.isFetching = false
                            }
                        },
                    },
                    {
                        title: this.$t('shared.no'),
                        handler: () => {
                            this.$modal.hide('dialog')
                        },
                    },
                ],
            })
        },
        handleEdit(user) {
            this.$modal.show(
                UserEditModal,
                { user, onSubmit: this.fetchUsers },
                { width: 400, height: '75%' }
            )
        },
        handleSort(column) {
            if (this.sortBy !== column || this.sortDirection !== 1) {
                this.sortBy = column
                this.sortDirection = 1
            } else if (this.sortDirection === 1) {
                this.sortDirection = -1
            }
        },
    },
}
</script>

<i18n>
{
    "en": {
        "actions": "Actions",
        "admin": "Admin",
        "delete": "Delete",
        "deleteActionTitle": "Delete user {0}",
        "userNotDeletableTitle": "User {0} cannot be deleted as some assets or locations are assigned to it.",
        "deleteUserError": "User {0} could not be deleted. Please try again or reach out to our support.",
        "deleteUserSuccess": "User {0} was deleted successfully.",
        "edit": "Edit",
        "editActionTitle": "Edit user {0}",
        "deleteUserConfirmation": "Are you sure you want to delete user {0}?",
        "firstName": "First Name",
        "lastName": "Last Name",
        "passwordResetNotification": "Password has been reset",
        "resetPassword": "Reset password",
        "resetPasswordActionTitle": "Reset password for user {0}",
        "role": "Role",
        "username": "Username"
    },
    "de": {
        "actions": "Aktionen",
        "admin": "Admin",
        "delete": "Löschen",
        "deleteActionTitle": "Benutzer {0} löschen",
        "userNotDeletableTitle": "Benutzer {0} kann nicht gelöscht werden, da ihm einige Assets oder Standorte zugewiesen sind.",
        "deleteUserError": "Benutzer {0} konnte nicht gelöscht werden. Bitte versuchen Sie es erneut oder kontaktieren Sie unseren Support.",
        "deleteUserSuccess": "Benutzer {0} wurde erfolgreich gelöscht.",
        "edit": "Bearbeiten",
        "editActionTitle": "Benutzer {0} bearbeiten",
        "deleteUserConfirmation": "Möchten Sie den Benutzer {0} wirklich löschen?",
        "firstName": "Vorname",
        "lastName": "Nachname",
        "passwordResetNotification": "Passwort wurde zurückgesetzt",
        "resetPassword": "Passwort zurücksetzen",
        "resetPasswordActionTitle": "Passwort für Benutzer {0} zurücksetzen",
        "role": "Rolle",
        "username": "Benutzername"
    },
    "fr": {
        "actions": "Actions",
        "admin": "Administrateur",
        "delete": "Supprimer",
        "deleteActionTitle": "Supprimer l'utilisateur {0}",
        "userNotDeletableTitle": "L'utilisateur {0} ne peut pas être supprimé, car certains actifs ou emplacements lui sont attribués.",
        "deleteUserError": "L'utilisateur {0} n'a pas pu être supprimé. Veuillez réessayer ou contacter notre support.",
        "deleteUserSuccess": "L'utilisateur {0} a été supprimé avec succès.",
        "edit": "Editer",
        "editActionTitle": "Modifier l'utilisateur {0}",
        "deleteUserConfirmation": "Voulez-vous vraiment supprimer l'utilisateur {0} ?",
        "firstName": "Prénom",
        "lastName": "Nom",
        "passwordResetNotification": "Mot de passe réinitialisé",
        "resetPassword": "Réinitialiser le mot de passe",
        "resetPasswordActionTitle": "Réinitialiser le mot de passe de l'utilisateur {0}",
        "role": "Rôle",
        "username": "Nom d'utilisateur"
    },
    "it": {
        "actions": "Azione",
        "admin": "Amministratore",
        "delete": "Cancellare",
        "deleteActionTitle": "Elimina utente {0}",
        "userNotDeletableTitle": "L'utente {0} non può essere eliminato perché gli sono assegnati alcuni asset o posizioni.",
        "deleteUserError": "L'utente {0} non può essere eliminato. Riprova o contatta il nostro supporto.",
        "deleteUserSuccess": "L'utente {0} è stato eliminato con successo.",
        "edit": "Condizione",
        "editActionTitle": "Modifica utente {0}",
        "deleteUserConfirmation": "Sei sicuro di voler eliminare l'utente {0}?",
        "firstName": "Prenome",
        "lastName": "Cognome",
        "passwordResetNotification": "La password è stata reimpostata",
        "resetPassword": "Reimpostare la password",
        "resetPasswordActionTitle": "Reimposta la password per l'utente {0}",
        "role": "Ruolo",
        "username": "Nome utente"
    }
}
</i18n>

<style lang="scss" scoped>
table {
    display: block;
    width: 100%;
    border-spacing: 0;
    font-size: 15px;
    color: rgba(0, 0, 0, 0.7);

    thead {
        position: sticky;
        top: 0;
        background-color: #fff;
        z-index: 1;

        .icon-button {
            margin-left: 5px;
        }
    }

    tr {
        &:not(:first-of-type) {
            td {
                border-top: $style-border;
            }
        }

        th {
            text-align: left;
            border-bottom: $style-border;
        }

        th,
        td {
            padding: 1rem;
            white-space: nowrap;

            &:first-child {
                padding-left: 2rem;
            }

            &:last-child {
                padding-right: 2rem;
            }

            &:not(:last-child) {
                width: 1%;
            }
        }
    }

    a {
        display: block;

        .icon-button {
            display: block;
        }
    }
}
</style>
